import React from "react";
import { StaticPage, Section, Bullets } from "../components/StaticPage";

export const PrivacyPolicy = () => (
  <StaticPage
    title="Privacy Policy"
    lastUpdated="September 5, 2026"
    testId="privacy-policy-page"
  >
    <p>
      Absolute Cinema ("Absolute Cinema", "we", "us", or "our") respects your privacy.
    </p>
    <p>
      This Privacy Policy explains how information may be collected, used, and handled when you
      visit or use the Absolute Cinema website.
    </p>
    <p>By using our website, you acknowledge the practices described in this Privacy Policy.</p>

    <Section heading="1. Information We Collect">
      <p>
        The information collected through Absolute Cinema depends on how you use the website and
        the technologies currently implemented on the website.
      </p>
      <p>
        When you visit the website, certain technical and usage information may be automatically
        processed by the website, hosting infrastructure, analytics services, or other third-party
        services.
      </p>
      <p>This may include information such as:</p>
      <Bullets items={[
        "IP address",
        "Browser type and version",
        "Device type",
        "Operating system",
        "Pages visited",
        "Date and time of visits",
        "Referring pages or websites",
        "General website usage and interaction information",
        "Technical information necessary to operate, secure, and improve the website",
      ]} />
      <p>
        We do not ask visitors to provide personal information simply to browse Absolute Cinema
        unless a particular feature or interaction specifically requires it.
      </p>
    </Section>

    <Section heading="2. How We Use Information">
      <p>Information may be used for purposes including:</p>
      <Bullets items={[
        "Operating and maintaining the website",
        "Providing website features",
        "Understanding how visitors use the website",
        "Improving website functionality",
        "Improving performance and usability",
        "Understanding website traffic and interactions",
        "Identifying and resolving technical problems",
        "Maintaining website security",
        "Preventing misuse",
        "Responding to inquiries and requests",
      ]} />
      <p>
        We aim to use information only for legitimate purposes related to operating, maintaining,
        improving, and supporting the website.
      </p>
    </Section>

    <Section heading="3. Google Analytics">
      <p>Absolute Cinema uses Google Analytics to understand how visitors use the website.</p>
      <p>
        Google Analytics may collect information about website visits and interactions, which can
        include information such as:
      </p>
      <Bullets items={[
        "Pages viewed",
        "Events and interactions",
        "Browser information",
        "Device information",
        "Approximate geographic information",
        "Website usage patterns",
      ]} />
      <p>
        We use analytics information to understand how visitors interact with the website, improve
        functionality, monitor performance, and improve the overall user experience.
      </p>
      <p>Google Analytics may use cookies and similar technologies in providing its analytics services.</p>
      <p>Google's handling of information is subject to Google's applicable policies and terms.</p>
    </Section>

    <Section heading="4. Cookies and Similar Technologies">
      <p>
        Absolute Cinema and third-party services used by the website may use cookies or similar
        technologies.
      </p>
      <p>
        Cookies are small files or pieces of information that may be stored on a user's device when
        using a website or service.
      </p>
      <p>Cookies or similar technologies may be used for purposes including:</p>
      <Bullets items={[
        "Website functionality",
        "Analytics",
        "Understanding website usage",
        "Measuring website performance",
        "Advertising, where applicable",
      ]} />
      <p>
        You can manage or disable cookies through your browser settings. Disabling certain cookies
        may affect the availability or functionality of some website features or third-party
        services.
      </p>
    </Section>

    <Section heading="5. Advertising">
      <p>
        Absolute Cinema may use advertising services, including Google AdSense, when advertising is
        enabled on the website.
      </p>
      <p>
        When Google advertising services are used, Google and its advertising partners may use
        cookies, web beacons, IP addresses, or similar technologies in connection with the
        delivery, measurement, and personalization of advertisements, where applicable.
      </p>
      <p>
        Third-party advertising providers may place or recognize cookies on a user's browser or use
        similar technologies when advertisements are displayed.
      </p>
      <p>
        Advertising practices may vary depending on factors such as the user's location, applicable
        laws, consent choices, and advertising settings.
      </p>
      <p>
        For information about Google's advertising practices, users should review Google's
        applicable privacy and advertising policies.
      </p>
    </Section>

    <Section heading="6. Third-Party Services">
      <p>
        Absolute Cinema may use third-party services to operate, analyze, maintain, improve, or
        support the website.
      </p>
      <p>
        These services may include services such as Google Analytics and, where applicable, Google
        advertising services.
      </p>
      <p>Third-party services may process information according to their own privacy policies and terms.</p>
      <p>We recommend reviewing the applicable policies of third-party services when using those services.</p>
    </Section>

    <Section heading="7. External Links">
      <p>Absolute Cinema may contain links or references to third-party websites or services.</p>
      <p>
        We do not control the privacy practices, security, content, or policies of third-party
        websites.
      </p>
      <p>
        When you visit an external website, we recommend reviewing its privacy policy and terms of
        service.
      </p>
    </Section>

    <Section heading="8. Data Security">
      <p>We take reasonable measures to help protect information handled through the website.</p>
      <p>
        However, no method of transmitting or storing information electronically can be guaranteed
        to be completely secure.
      </p>
    </Section>

    <Section heading="9. Data Retention">
      <p>
        Information is retained only for as long as reasonably necessary for the purposes for which
        it is processed, or as necessary for legitimate technical, security, operational, or legal
        purposes.
      </p>
      <p>
        Third-party services used by Absolute Cinema may retain information according to their own
        applicable policies.
      </p>
    </Section>

    <Section heading="10. Children's Privacy">
      <p>Absolute Cinema does not knowingly seek to collect personal information from children.</p>
      <p>
        If you believe that a child has provided personal information to us, please contact us using
        the information provided below so that the matter can be reviewed.
      </p>
    </Section>

    <Section heading="11. Privacy Choices">
      <p>
        Depending on your location and applicable law, you may have rights regarding your personal
        information.
      </p>
      <p>
        You may also manage certain privacy and cookie settings through your browser and, where
        applicable, through privacy or advertising controls provided by third-party services.
      </p>
    </Section>

    <Section heading="12. Changes to This Privacy Policy">
      <p>
        We may update this Privacy Policy from time to time to reflect changes to the website,
        technologies, services, or applicable requirements.
      </p>
      <p>
        When this Privacy Policy is updated, the Last Updated date at the top of this page will be
        updated.
      </p>
    </Section>

    <Section heading="13. Contact Us">
      <p>
        If you have questions or concerns about this Privacy Policy or how information is handled by
        Absolute Cinema, please contact us:
      </p>
      <p>
        Email:{" "}
        <a href="mailto:absolutecinema2503@gmail.com" className="ac-gold-text hover:underline">
          absolutecinema2503@gmail.com
        </a>
      </p>
    </Section>
  </StaticPage>
);
